import * as yup from 'yup'
import {
  requiredString,
  requiredInteger,
  requiredBool,
  requiredDate,
  requiredDatePeriod,
  pageNum,
  pageSize,
  options,
  currentDate,
  getFutureDate,
  state,
  role,
  userId,
  status,
  stringNullable,
  date
} from '../yup/schemas.js'

import { JOBS_BOARD_TYPES, VALIDATION } from '../constants.js'
import allowedValues from './allowed.values.js'
const {
  carTypesAll, carTypeForEquipmentExceptNA, carTypeForEquipmentNA, carTypeForEquipmentAny,
  typeOfEquipmentAll, typeOfEquipmentNA, typeOfEquipmentExceptNA, IS_EMPLOYED, isEmployed
} = allowedValues

const { MESSAGES, REGEXP } = VALIDATION

const applicationSchema = yup.object().shape({
  id: requiredString.uuid(),
  lastName: requiredString,
  firstName: requiredString,
  middleName: yup.string().optional(),
  email: requiredString.email(),
  socialSecurityNumber: requiredString.matches(REGEXP.SOCIAL_SECURITY_NUMBER, MESSAGES.SOCIAL_SECURITY_NUMBER),
  phone: requiredString.matches(REGEXP.PHONE, MESSAGES.PHONE),
  dateOfBirth: requiredDate.max(currentDate, MESSAGES.DATE),
  pastResidency: options.of(
    yup.object().shape({
      street: requiredString,
      city: requiredString,
      state: state.required(),
      zip: requiredString.zip(),
      numberOfYears: requiredDatePeriod.dateRange()
    }).objectRequired()
  ),

  employmentHistory: requiredBool,
  employmentHistoryInfo: options.of(
    yup.object().shape({
      isEmployed: requiredString.oneOf(isEmployed),
      employmentInfo: yup.object().shape({
        name: requiredString,
        phone: requiredString.matches(REGEXP.PHONE, MESSAGES.PHONE),
        street: requiredString,
        city: requiredString,
        state: state.required(),
        zip: requiredString.zip(),
        position: requiredString,
        period: requiredDatePeriod.dateRange(),
        reasonForLeaving: requiredString,
        subjectOfFederalMotorCarrierSafetyRegulations: requiredBool,
        subjectOfDrugAndAlcoholTesting: requiredBool
      }).existsWhen('isEmployed', IS_EMPLOYED.EMPLOYED),
      unemploymentInfo: yup.object().shape({
        periodBetweenJobs: requiredDatePeriod.dateRange(),
        periodBetweenJobsReason: requiredString
      }).existsWhen('isEmployed', IS_EMPLOYED.UNEMPLOYED)
    })
  ).existsWhen('employmentHistory', false),

  drivingExperienceInLastThreeYears: requiredBool,
  drivingExperienceInfo: options.of(yup.object().shape({
    classOfEquipment: requiredString.oneOf(carTypesAll),
    typeOfEquipment: yup.mixed().when('classOfEquipment', (classOfEquipment) => {
      if (carTypeForEquipmentExceptNA.includes(classOfEquipment)) {
        return options.of(yup.string().oneOf(typeOfEquipmentExceptNA))
      } else if (carTypeForEquipmentNA.includes(classOfEquipment)) {
        return options.of(yup.string().oneOf(typeOfEquipmentNA))
      } else if (carTypeForEquipmentAny.includes(classOfEquipment)) {
        return options.of(yup.string().oneOf(typeOfEquipmentAll))
      }
    }),
    dates: requiredBool,
    period: requiredDatePeriod.dateRange().existsWhen('dates', true),
    numberOfMiles: requiredInteger.min(1, VALIDATION.MESSAGES.MIN(1)).existsWhen('dates', false)
  })
  ).existsWhen('drivingExperienceInLastThreeYears', true),
  affiliatedSchool: stringNullable,
  graduatedDate: date.max(getFutureDate(30), MESSAGES.DATE),

  accidentHistory: requiredBool,
  accidentHistoryInfo: options.of(
    yup.object().shape({
      date: requiredDate.max(currentDate, MESSAGES.DATE),
      natureOfAccident: requiredString,
      numberOfFatalities: requiredInteger.min(0, VALIDATION.MESSAGES.MIN(0)),
      numberOfInjuries: requiredInteger.min(0, VALIDATION.MESSAGES.MIN(0)),
      hazardousMaterialsSpill: requiredBool
    })
  ).existsWhen('accidentHistory', true),

  trafficConvictionsAndForfeitures: requiredBool,
  trafficConvictionsAndForfeituresInfo: options.of(
    yup.object().shape({
      date: requiredDate.max(currentDate, MESSAGES.DATE),
      violation: requiredString,
      stateOfViolation: requiredString,
      penalty: requiredString
    })
  ).existsWhen('trafficConvictionsAndForfeitures', true),

  licenseState: state.required(),
  licenseNumber: requiredString,
  licenseExperation: requiredDate.max(getFutureDate(43), MESSAGES.DATE).min(currentDate, MESSAGES.DATE),
  licenseClass: requiredString,
  wasLicenseDenied: requiredBool,
  licenseDeniedInfo: requiredString.existsWhen('wasLicenseDenied', true),
  wasLicenseRevoked: requiredBool,
  licenseRevokedInfo: requiredString.existsWhen('wasLicenseRevoked', true),
  noMoreThanOneLicense: requiredBool.oneOf([true], 'Field must be checked'),

  signature: yup.string().required('Your signature is required'),
  boardType: requiredString.oneOf([JOBS_BOARD_TYPES.ENTRY, JOBS_BOARD_TYPES.EXPERIENCED]).defaultIfRequired(JOBS_BOARD_TYPES.ENTRY),
  studentId: requiredString.uuid(),
  isSaveApplicationData: yup.boolean()
}).objectRequired()

const createApplicationSchemaFront = [
  applicationSchema
    .pick([
      'lastName',
      'firstName',
      'middleName',
      'email',
      'socialSecurityNumber',
      'phone',
      'dateOfBirth'
    ]),
  applicationSchema
    .pick([
      'currentAddressStreet',
      'currentAddressCity',
      'currentAddressState',
      'currentAddressZip',
      'currentAddressNumberOfYears',
      'pastResidency'
    ]),

  applicationSchema
    .pick([
      'employmentHistory',
      'employmentHistoryInfo'
    ]),

  applicationSchema
    .pick([
      'accidentHistory',
      'accidentHistoryInfo']),

  applicationSchema
    .pick([
      'drivingExperienceInLastThreeYears',
      'drivingExperienceInfo',
      'affiliatedSchool',
      'graduatedDate'
    ]),

  applicationSchema
    .pick([
      'trafficConvictionsAndForfeitures',
      'trafficConvictionsAndForfeituresInfo']),

  applicationSchema
    .pick([
      'licenseState',
      'licenseNumber',
      'licenseExperation',
      'licenseClass',
      'wasLicenseDenied',
      'licenseDeniedInfo',
      'wasLicenseRevoked',
      'licenseRevokedInfo',
      'noMoreThanOneLicense']),

  applicationSchema
    .pick(['signature']),

  applicationSchema
    .pick(['studentId']),

  applicationSchema
    .pick(['isSaveApplicationData'])
]
const createApplicationSchema = applicationSchema.omit(['id'])
const createApplicationSchemas = { front: createApplicationSchemaFront, back: createApplicationSchema }

const applicationListQueryParamsSchema = yup.object({
  pageNum,
  pageSize,
  userId,
  role,
  status
}).objectRequired()

const statusApplicationParamsSchema = yup.object({
  id: requiredString.uuid(),
  companyId: requiredString.uuid()
}).objectRequired()

const getApplicationParamsSchema = applicationSchema.pick(['id'])

const applicationListQueryParamsSchemas = { front: applicationListQueryParamsSchema, back: applicationListQueryParamsSchema }
const statusApplicationParams = { front: statusApplicationParamsSchema, back: statusApplicationParamsSchema }
const getApplicationParamsSchemas = { front: getApplicationParamsSchema, back: getApplicationParamsSchema }

export default {
  createApplication: createApplicationSchemas,
  getJobApplicationListQueryParams: applicationListQueryParamsSchemas,
  getJobApplicationParams: getApplicationParamsSchemas,
  statusApplicationParams: statusApplicationParams,
  allowedValues
}
